/**
 * GovernanceAuthorizationsPage — Unified authorization center for superadmin
 * 
 * Consolidates:
 * - Authorization requests (generic approval workflow)
 * - Pending user registrations
 * - Club official claims (approval/rejection)
 * 
 * Backend endpoints:
 * - GET /governance/authorization/dashboard
 * - GET /governance/authorization/requests?status=pending
 * - POST /governance/authorization/requests/:id/approve
 * - POST /governance/authorization/requests/:id/reject
 * - GET /auth/admin/pending-users
 * - POST /auth/admin/approve-user
 * - GET /claims/admin/pending (club claims)
 * - PUT /claims/admin/:id/approve
 * - PUT /claims/admin/:id/reject
 * - GET /governance/commercial/access-requests (tipo CLUBE — mesma fonte do Comercial)
 * - PATCH /governance/commercial/access-requests/:id/status (mesma máquina de estados P1.4)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, UserCheck, Building2, Clock, CheckCircle2, XCircle, AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { AZURE } from '../../services/api';
import { patchAccessRequestStatus } from '../../services/accessRequestsService';
import ProvisionAccessModal from '../../components/governance/ProvisionAccessModal';
import { COLORS, STYLES, TYPOGRAPHY } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const AUTH_LABELS = {
  pt: {
    title: 'Autorizações',
    subtitle: 'Aprovações pendentes e histórico de decisões',
    refresh: 'Atualizar',
    loadingErr: 'Erro ao carregar autorizações',
    statPending: 'Pendentes',
    statClaims: 'Vínculos de Clube',
    statUsers: 'Registros',
    statAuth: 'Autorizações',
    tabAll: (n) => `Todos (${n})`,
    tabClaims: (n) => `Vínculos (${n})`,
    tabUsers: (n) => `Usuários (${n})`,
    tabAuthorizations: (n) => `Autorizações (${n})`,
    emptyTitle: 'Nenhuma pendência',
    emptySubtitle: 'Todas as autorizações foram processadas.',
    rejectModalTitle: 'Motivo da Rejeição',
    rejectPlaceholder: 'Descreva o motivo (opcional)...',
    cancel: 'Cancelar',
    reject: 'Rejeitar',
    approve: 'Aprovar',
    claimsSectionTitle: 'Vínculos de Clube Pendentes',
    thRequester: 'Solicitante',
    thClub: 'Clube',
    thRole: 'Cargo',
    thDate: 'Data',
    thActions: 'Ações',
    usersSectionTitle: 'Registros de Usuários Pendentes',
    thName: 'Nome',
    thEmail: 'Email',
    thType: 'Tipo',
    authSectionTitle: 'Solicitações de Autorização',
    thTypeCol: 'Tipo',
    thDescription: 'Descrição',
    clubSectionTitle: 'Solicitações de Acesso — Clubes',
    clubApprovedTitle: 'Aprovadas — aguardando provisionamento',
    clubConvertedTitle: 'Concluídas',
    clubRejectedTitle: 'Rejeitadas',
    provisionCta: 'Provisionar clube',
    viewRequest: 'Ver solicitação',
    statClubs: 'Clubes',
    tabClubs: (n) => `Clubes (${n})`,
    thRequest: 'Solicitação',
    thResponsible: 'Responsável',
    thStatus: 'Status',
    review: 'Analisar',
    waitInfo: 'Aguardar informações',
    view: 'Ver',
    errClubStatus: (e) => 'Erro ao atualizar solicitação: ' + e,
    errApprove: (e) => 'Erro ao aprovar: ' + e,
    errReject: (e) => 'Erro ao rejeitar: ' + e,
    errApproveUser: (e) => 'Erro ao aprovar usuário: ' + e,
    errApproveClaim: (e) => 'Erro ao aprovar vínculo: ' + e,
    errRejectClaim: (e) => 'Erro ao rejeitar vínculo: ' + e,
  },
  en: {
    title: 'Authorizations',
    subtitle: 'Pending approvals and decision history',
    refresh: 'Refresh',
    loadingErr: 'Error loading authorizations',
    statPending: 'Pending',
    statClaims: 'Club Links',
    statUsers: 'Registrations',
    statAuth: 'Authorizations',
    tabAll: (n) => `All (${n})`,
    tabClaims: (n) => `Links (${n})`,
    tabUsers: (n) => `Users (${n})`,
    tabAuthorizations: (n) => `Authorizations (${n})`,
    emptyTitle: 'Nothing pending',
    emptySubtitle: 'All authorizations have been processed.',
    rejectModalTitle: 'Rejection Reason',
    rejectPlaceholder: 'Describe the reason (optional)...',
    cancel: 'Cancel',
    reject: 'Reject',
    approve: 'Approve',
    claimsSectionTitle: 'Pending Club Links',
    thRequester: 'Requester',
    thClub: 'Club',
    thRole: 'Role',
    thDate: 'Date',
    thActions: 'Actions',
    usersSectionTitle: 'Pending User Registrations',
    thName: 'Name',
    thEmail: 'Email',
    thType: 'Type',
    authSectionTitle: 'Authorization Requests',
    thTypeCol: 'Type',
    thDescription: 'Description',
    clubSectionTitle: 'Access Requests — Clubs',
    clubApprovedTitle: 'Approved — awaiting provisioning',
    clubConvertedTitle: 'Completed',
    clubRejectedTitle: 'Rejected',
    provisionCta: 'Provision club',
    viewRequest: 'View request',
    statClubs: 'Clubs',
    tabClubs: (n) => `Clubs (${n})`,
    thRequest: 'Request',
    thResponsible: 'Responsible',
    thStatus: 'Status',
    review: 'Review',
    waitInfo: 'Request info',
    view: 'View',
    errClubStatus: (e) => 'Error updating request: ' + e,
    errApprove: (e) => 'Error approving: ' + e,
    errReject: (e) => 'Error rejecting: ' + e,
    errApproveUser: (e) => 'Error approving user: ' + e,
    errApproveClaim: (e) => 'Error approving link: ' + e,
    errRejectClaim: (e) => 'Error rejecting link: ' + e,
  },
};

// ─── Status metadata ───────────────────────────────────────────────────────
const STATUS_META = {
  pending: { label: 'Pendente', bg: '#fef3c7', text: '#92400e', icon: Clock },
  approved: { label: 'Aprovado', bg: '#d1fae5', text: '#065f46', icon: CheckCircle2 },
  rejected: { label: 'Rejeitado', bg: '#fee2e2', text: '#991b1b', icon: XCircle },
  review: { label: 'Em análise', bg: '#dbeafe', text: '#1e40af', icon: Eye },
  NEW: { label: 'Novo', bg: '#fef3c7', text: '#92400e', icon: Clock },
  IN_REVIEW: { label: 'Em análise', bg: '#dbeafe', text: '#1e40af', icon: Eye },
  MORE_INFO_REQUIRED: { label: 'Aguardando info', bg: '#fae8ff', text: '#86198f', icon: AlertTriangle },
  APPROVED: { label: 'Aprovado', bg: '#ecfdf5', text: '#059669', icon: CheckCircle2 },
  REJECTED: { label: 'Rejeitado', bg: '#fee2e2', text: '#991b1b', icon: XCircle },
  CONVERTED: { label: 'Convertido', bg: '#d1fae5', text: '#065f46', icon: CheckCircle2 },
};

const TYPE_META = {
  club_claim: { label: 'Vínculo de Clube', icon: Building2, color: '#7c3aed' },
  user_registration: { label: 'Registro de Usuário', icon: UserCheck, color: '#0891b2' },
  authorization: { label: 'Autorização', icon: Shield, color: '#111111' },
};

// ─── Small components ──────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold"
      style={{ backgroundColor: meta.bg, color: meta.text }}>
      <meta.icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={STYLES.card + ' p-4 flex items-center gap-3'}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <p className="text-2xl font-extrabold" style={{ color: COLORS.text }}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────
const GovernanceAuthorizationsPage = () => {
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [dashboard, setDashboard] = useState({ pending: 0, approved_today: 0, rejected_today: 0 });
  const [authRequests, setAuthRequests] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingClaims, setPendingClaims] = useState([]);
  // Tipo CLUBE: projeção de commercial_access_requests (mesma fonte do
  // Comercial → Solicitações). Central de processo inteiro:
  //   Pendentes = NEW/IN_REVIEW/MORE_INFO · Aprovadas = APPROVED (aguardando
  //   provisionamento) · Concluídas = CONVERTED · Rejeitadas = REJECTED.
  const [clubRequests, setClubRequests] = useState([]);
  const [provisionTarget, setProvisionTarget] = useState(null); // request APPROVED em provisionamento

  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);

  const { lang } = useGovernanceLang();
  const t = AUTH_LABELS[lang] || AUTH_LABELS.pt;

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [dashRes, reqRes, usersRes, claimsRes, clubRes] = await Promise.allSettled([
        AZURE.get('/governance/authorization/dashboard'),
        AZURE.get('/governance/authorization/requests', { params: { status: 'pending', limit: 50 } }),
        AZURE.get('/auth/admin/pending-users'),
        AZURE.get('/claims/admin/pending'),
        AZURE.get('/governance/commercial/access-requests', { params: { limit: 200 } }),
      ]);

      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data?.data || dashRes.value.data || {});
      if (reqRes.status === 'fulfilled') setAuthRequests(reqRes.value.data?.data || reqRes.value.data?.requests || []);
      if (usersRes.status === 'fulfilled') setPendingUsers(usersRes.value.data?.data || usersRes.value.data?.users || []);
      if (claimsRes.status === 'fulfilled') setPendingClaims(claimsRes.value.data?.data || claimsRes.value.data?.claims || []);
      if (clubRes.status === 'fulfilled') {
        setClubRequests(clubRes.value.data?.data || []);
      }
    } catch (err) {
      setError(t.loadingErr || 'Erro ao carregar autorizações');
      console.error('[Authorizations] fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const approveRequest = async (id) => {
    setActionLoading(id);
    try {
      await AZURE.post(`/governance/authorization/requests/${id}/approve`);
      setAuthRequests(prev => prev.filter(r => r.id !== id));
      setDashboard(prev => ({ ...prev, pending: Math.max(0, (prev.pending || 0) - 1) }));
    } catch (err) {
      alert(t.errApprove(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const rejectRequest = async (id) => {
    setActionLoading(id);
    try {
      await AZURE.post(`/governance/authorization/requests/${id}/reject`, { reason: rejectReason });
      setAuthRequests(prev => prev.filter(r => r.id !== id));
      setDashboard(prev => ({ ...prev, pending: Math.max(0, (prev.pending || 0) - 1) }));
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      alert(t.errReject(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const approveUser = async (userId) => {
    setActionLoading(`user-${userId}`);
    try {
      await AZURE.post('/auth/admin/approve-user', { user_id: userId });
      setPendingUsers(prev => prev.filter(u => (u.id || u.user_id) !== userId));
    } catch (err) {
      alert(t.errApproveUser(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const approveClaim = async (claimId) => {
    setActionLoading(`claim-${claimId}`);
    try {
      await AZURE.put(`/claims/admin/${claimId}/approve`);
      setPendingClaims(prev => prev.filter(c => (c.id || c.claim_id) !== claimId));
    } catch (err) {
      alert(t.errApproveClaim(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const rejectClaim = async (claimId) => {
    setActionLoading(`claim-${claimId}`);
    try {
      await AZURE.put(`/claims/admin/${claimId}/reject`, { reason: rejectReason });
      setPendingClaims(prev => prev.filter(c => (c.id || c.claim_id) !== claimId));
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      alert(t.errRejectClaim(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  // Tipo CLUBE: uma decisão, uma autoridade — mesmo PATCH e máquina de
  // estados do Comercial (NEW→IN_REVIEW; IN_REVIEW→APPROVED/REJECTED/MORE_INFO).
  const patchClubStatus = async (requestId, toStatus, note = '') => {
    setActionLoading(`club-${requestId}`);
    try {
      await patchAccessRequestStatus(requestId, toStatus, note || undefined);
      // Nunca some da central — apenas muda de área (Pendentes/Aprovadas/Rejeitadas)
      setClubRequests(prev => prev.map(r => r.request_id === requestId ? { ...r, status: toStatus } : r));
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      alert(t.errClubStatus(err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  // P2: provisionamento concluído → APPROVED vira CONVERTED sem refresh manual.
  const onProvisioned = (requestId) => {
    setClubRequests(prev => prev.map(r => r.request_id === requestId ? { ...r, status: 'CONVERTED' } : r));
  };

  // ─── Filter by tab ─────────────────────────────────────────────────────────────
  const clubPending = clubRequests.filter(r => ['NEW', 'IN_REVIEW', 'MORE_INFO_REQUIRED'].includes(r.status));
  const clubApproved = clubRequests.filter(r => r.status === 'APPROVED');
  const clubConverted = clubRequests.filter(r => r.status === 'CONVERTED');
  const clubRejected = clubRequests.filter(r => r.status === 'REJECTED');
  const totalPending = clubPending.length + authRequests.length + pendingUsers.length + pendingClaims.length;

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: COLORS.primary }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className={TYPOGRAPHY.pageTitle} style={{ color: COLORS.text }}>{t.title}</h1>
            <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
          </div>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          className={STYLES.buttonSecondary + ' flex items-center gap-2'}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t.refresh}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Clock} label={t.statPending} value={totalPending} color="#d97706" />
        <StatCard icon={Building2} label={t.statClubs} value={clubPending.length} color="#064e3b" />
        <StatCard icon={Building2} label={t.statClaims} value={pendingClaims.length} color="#7c3aed" />
        <StatCard icon={UserCheck} label={t.statUsers} value={pendingUsers.length} color="#0891b2" />
      </div>

      {/* Tabs */}
      <div className={STYLES.tabContainer + ' mb-6'}>
        {[
          { id: 'all', label: t.tabAll(totalPending) },
          { id: 'clubs', label: t.tabClubs(clubPending.length + clubApproved.length) },
          { id: 'claims', label: t.tabClaims(pendingClaims.length) },
          { id: 'users', label: t.tabUsers(pendingUsers.length) },
          { id: 'authorizations', label: t.tabAuthorizations(authRequests.length) },
        ].map(tb => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`${STYLES.tab} ${tab === tb.id ? STYLES.tabActive : STYLES.tabInactive}`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {totalPending === 0 && clubApproved.length === 0 && (
        <div className={STYLES.card + ' ' + STYLES.emptyState}>
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-lg font-bold text-gray-700">{t.emptyTitle}</p>
          <p className="text-sm text-gray-500 mt-1">{t.emptySubtitle}</p>
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className={STYLES.card + ' w-full max-w-md p-6'}>
            <h3 className="text-lg font-bold text-gray-900 mb-3">{t.rejectModalTitle}</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t.rejectPlaceholder}
              className={STYLES.textarea + ' mb-4'}
              rows={3}
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} className={STYLES.buttonSecondary}>
                {t.cancel}
              </button>
              <button
                onClick={() => {
                  if (rejectTarget.type === 'claim') rejectClaim(rejectTarget.id);
                  else if (rejectTarget.type === 'club') patchClubStatus(rejectTarget.id, 'REJECTED', rejectReason);
                  else rejectRequest(rejectTarget.id);
                }}
                className={STYLES.buttonDanger}
                disabled={actionLoading === rejectTarget.id}
              >
                {t.reject}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Club Access Requests — PENDENTES (NEW/IN_REVIEW/MORE_INFO) */}
      {(tab === 'all' || tab === 'clubs') && clubPending.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: '#064e3b' }} />
            {t.clubSectionTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <thead className={STYLES.tableHeader}>
                <tr>
                  <th className={STYLES.tableHeaderCell}>{t.thTypeCol}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thRequest}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thResponsible}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thDate}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thStatus}</th>
                  <th className={STYLES.tableHeaderCell + ' text-right'}>{t.thActions}</th>
                </tr>
              </thead>
              <tbody>
                {clubPending.map(req => {
                  const id = req.request_id;
                  const busy = actionLoading === `club-${id}`;
                  const place = [req.city, req.state].filter(Boolean).join(', ');
                  return (
                    <tr key={id} className={STYLES.tableRow}>
                      <td className={STYLES.tableCell}>
                        <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: '#064e3b15', color: '#064e3b' }}>
                          CLUBE
                        </span>
                      </td>
                      <td className={STYLES.tableCell + ' font-medium'}>
                        {req.club_name}
                        {place && <span className="block text-xs text-gray-400 font-normal">{place}</span>}
                        {req.plan_code && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: '#ecfdf5', color: '#065f46' }}>
                            {req.plan_code}
                          </span>
                        )}
                      </td>
                      <td className={STYLES.tableCell}>
                        {req.contact_name || '—'}
                        {req.email && <span className="block text-xs text-gray-400">{req.email}</span>}
                      </td>
                      <td className={STYLES.tableCell + ' text-gray-500'}>{req.created_at ? new Date(req.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className={STYLES.tableCell}><StatusBadge status={req.status} /></td>
                      <td className={STYLES.tableCell + ' text-right'}>
                        <div className="flex items-center justify-end gap-2">
                          {req.status === 'NEW' && (
                            <button
                              onClick={() => patchClubStatus(id, 'IN_REVIEW')}
                              disabled={busy}
                              className="px-3 py-1.5 text-xs font-bold text-white rounded-lg transition-colors"
                              style={{ backgroundColor: '#1e40af' }}
                            >
                              {busy ? '...' : t.review}
                            </button>
                          )}
                          {(req.status === 'IN_REVIEW' || req.status === 'MORE_INFO_REQUIRED') && (
                            <>
                              <button
                                onClick={() => patchClubStatus(id, 'APPROVED')}
                                disabled={busy}
                                className="px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                              >
                                {busy ? '...' : t.approve}
                              </button>
                              {req.status === 'IN_REVIEW' && (
                                <button
                                  onClick={() => patchClubStatus(id, 'MORE_INFO_REQUIRED')}
                                  disabled={busy}
                                  className="px-3 py-1.5 text-xs font-bold text-purple-700 border border-purple-200 hover:bg-purple-50 rounded-lg transition-colors"
                                >
                                  {t.waitInfo}
                                </button>
                              )}
                              <button
                                onClick={() => setRejectTarget({ type: 'club', id })}
                                className="px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                {t.reject}
                              </button>
                            </>
                          )}
                          <Link
                            to={`/governance/comercial/solicitacoes?request=${id}`}
                            className="px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            {t.view}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Club Access Requests — APROVADAS aguardando provisionamento (P2) */}
      {(tab === 'all' || tab === 'clubs') && clubApproved.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" style={{ color: '#059669' }} />
            {t.clubApprovedTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <tbody>
                {clubApproved.map(req => {
                  const place = [req.city, req.state].filter(Boolean).join(', ');
                  return (
                    <tr key={req.request_id} className={STYLES.tableRow}>
                      <td className={STYLES.tableCell}>
                        <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: '#064e3b15', color: '#064e3b' }}>CLUBE</span>
                      </td>
                      <td className={STYLES.tableCell + ' font-medium'}>
                        {req.club_name}
                        {place && <span className="block text-xs text-gray-400 font-normal">{place}</span>}
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={req.plan_code ? { backgroundColor: '#ecfdf5', color: '#065f46' } : { backgroundColor: '#fffbeb', color: '#92400e' }}>
                          {req.plan_code || 'sem plano'}
                        </span>
                      </td>
                      <td className={STYLES.tableCell}>
                        {req.contact_name || '—'}
                        {req.email && <span className="block text-xs text-gray-400">{req.email}</span>}
                      </td>
                      <td className={STYLES.tableCell}><StatusBadge status="APPROVED" /></td>
                      <td className={STYLES.tableCell + ' text-right'}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setProvisionTarget(req)}
                            className="px-3 py-1.5 text-xs font-bold text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#064e3b' }}
                          >
                            {t.provisionCta}
                          </button>
                          <Link
                            to={`/governance/comercial/solicitacoes?request=${req.request_id}`}
                            className="px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            {t.view}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Club Access Requests — CONCLUÍDAS (CONVERTED) */}
      {tab === 'clubs' && clubConverted.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" style={{ color: '#065f46' }} />
            {t.clubConvertedTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <tbody>
                {clubConverted.map(req => (
                  <tr key={req.request_id} className={STYLES.tableRow}>
                    <td className={STYLES.tableCell + ' font-medium'}>{req.club_name}</td>
                    <td className={STYLES.tableCell}>{req.contact_name || '—'}</td>
                    <td className={STYLES.tableCell}><StatusBadge status="CONVERTED" /></td>
                    <td className={STYLES.tableCell + ' text-right'}>
                      {/* Destino administrativo do tenant ainda não definido —
                          por ora, “Ver solicitação” abre o detalhe Comercial. */}
                      <Link
                        to={`/governance/comercial/solicitacoes?request=${req.request_id}`}
                        className="px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {t.viewRequest}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Club Access Requests — REJEITADAS */}
      {tab === 'clubs' && clubRejected.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <XCircle className="w-4 h-4" style={{ color: '#991b1b' }} />
            {t.clubRejectedTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <tbody>
                {clubRejected.map(req => (
                  <tr key={req.request_id} className={STYLES.tableRow}>
                    <td className={STYLES.tableCell + ' font-medium'}>{req.club_name}</td>
                    <td className={STYLES.tableCell}>{req.contact_name || '—'}</td>
                    <td className={STYLES.tableCell}><StatusBadge status="REJECTED" /></td>
                    <td className={STYLES.tableCell + ' text-right'}>
                      <Link
                        to={`/governance/comercial/solicitacoes?request=${req.request_id}`}
                        className="px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {t.viewRequest}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Modal de provisionamento (P2) — preview obrigatório antes de confirmar */}
      {provisionTarget && (
        <ProvisionAccessModal
          request={provisionTarget}
          lang={lang}
          onClose={() => setProvisionTarget(null)}
          onProvisioned={onProvisioned}
        />
      )}

      {/* Club Claims Section */}
      {(tab === 'all' || tab === 'claims') && pendingClaims.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: '#7c3aed' }} />
            {t.claimsSectionTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <thead className={STYLES.tableHeader}>
                <tr>
                  <th className={STYLES.tableHeaderCell}>{t.thRequester}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thClub}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thRole}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thDate}</th>
                  <th className={STYLES.tableHeaderCell + ' text-right'}>{t.thActions}</th>
                </tr>
              </thead>
              <tbody>
                {pendingClaims.map(claim => {
                  const id = claim.id || claim.claim_id;
                  return (
                    <tr key={id} className={STYLES.tableRow}>
                      <td className={STYLES.tableCell + ' font-medium'}>{claim.user_name || claim.user_email || `User #${claim.user_id}`}</td>
                      <td className={STYLES.tableCell}>{claim.club_name || `Club #${claim.club_id}`}</td>
                      <td className={STYLES.tableCell}>{claim.role || claim.position || '—'}</td>
                      <td className={STYLES.tableCell + ' text-gray-500'}>{claim.created_at ? new Date(claim.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className={STYLES.tableCell + ' text-right'}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => approveClaim(id)}
                            disabled={actionLoading === `claim-${id}`}
                            className="px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                          >
                            {actionLoading === `claim-${id}` ? '...' : t.approve}
                          </button>
                          <button
                            onClick={() => setRejectTarget({ type: 'claim', id })}
                            className="px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            {t.reject}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Pending Users Section */}
      {(tab === 'all' || tab === 'users') && pendingUsers.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <UserCheck className="w-4 h-4" style={{ color: '#0891b2' }} />
            {t.usersSectionTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <thead className={STYLES.tableHeader}>
                <tr>
                  <th className={STYLES.tableHeaderCell}>{t.thName}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thEmail}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thType}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thDate}</th>
                  <th className={STYLES.tableHeaderCell + ' text-right'}>{t.thActions}</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map(user => {
                  const id = user.id || user.user_id;
                  return (
                    <tr key={id} className={STYLES.tableRow}>
                      <td className={STYLES.tableCell + ' font-medium'}>{user.name || user.full_name || '—'}</td>
                      <td className={STYLES.tableCell}>{user.email}</td>
                      <td className={STYLES.tableCell}>{user.role || user.user_type || '—'}</td>
                      <td className={STYLES.tableCell + ' text-gray-500'}>{user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                      <td className={STYLES.tableCell + ' text-right'}>
                        <button
                          onClick={() => approveUser(id)}
                          disabled={actionLoading === `user-${id}`}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        >
                          {actionLoading === `user-${id}` ? '...' : t.approve}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Authorization Requests Section */}
      {(tab === 'all' || tab === 'authorizations') && authRequests.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" style={{ color: '#111' }} />
            {t.authSectionTitle}
          </h2>
          <div className={STYLES.card + ' overflow-hidden'}>
            <table className={STYLES.table}>
              <thead className={STYLES.tableHeader}>
                <tr>
                  <th className={STYLES.tableHeaderCell}>{t.thTypeCol}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thRequester}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thDescription}</th>
                  <th className={STYLES.tableHeaderCell}>{t.thDate}</th>
                  <th className={STYLES.tableHeaderCell + ' text-right'}>{t.thActions}</th>
                </tr>
              </thead>
              <tbody>
                {authRequests.map(req => (
                  <tr key={req.id} className={STYLES.tableRow}>
                    <td className={STYLES.tableCell}>
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-700">
                        {req.request_type || req.type || 'general'}
                      </span>
                    </td>
                    <td className={STYLES.tableCell + ' font-medium'}>{req.requester_name || req.requester_email || `#${req.requester_id}`}</td>
                    <td className={STYLES.tableCell + ' text-gray-600 max-w-xs truncate'}>{req.description || req.reason || '—'}</td>
                    <td className={STYLES.tableCell + ' text-gray-500'}>{req.created_at ? new Date(req.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                    <td className={STYLES.tableCell + ' text-right'}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => approveRequest(req.id)}
                          disabled={actionLoading === req.id}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        >
                          {actionLoading === req.id ? '...' : t.approve}
                        </button>
                        <button
                          onClick={() => setRejectTarget({ type: 'request', id: req.id })}
                          className="px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          {t.reject}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default GovernanceAuthorizationsPage;
